export interface ChatDeliveryEntry {id?:string;role?:unknown;timestamp?:unknown;deliveredAt?:number;deliveredAfterIds?:string[];tool_calls?:unknown;tool_call_id?:unknown}
const timeOf=(entry:ChatDeliveryEntry)=>typeof entry.timestamp==='number'?entry.timestamp:Number(entry.id?.match(/(\d{13})(?:\D.*)?$/)?.[1]??0);
/** Move delivered answers in completion order. A tool-call answer and its exact
 * paired results move as one block so ordering cannot corrupt the protocol. */
export function orderChatDeliveries<T extends ChatDeliveryEntry>(rows:T[]):T[]{
 const paired=new Map<T,T[]>();
 const callCounts=new Map<string,number>();
 for(const row of rows)if(row.role==='assistant'&&Array.isArray(row.tool_calls))for(const call of row.tool_calls)
  if(typeof call?.id==='string')callCounts.set(call.id,(callCounts.get(call.id)??0)+1);
 for(const row of rows.filter(row=>row.role==='assistant')){
  if(!Array.isArray(row.tool_calls)||row.tool_calls.length===0){paired.set(row,[row]);continue;}
  const ids=row.tool_calls.map(call=>call?.id);
  if(ids.some(id=>typeof id!=='string'||!id)||new Set(ids).size!==ids.length)continue;
  // Ambiguous/incomplete pairings remain in place; never borrow another call's result.
  if(ids.some(id=>callCounts.get(id)!==1))continue;
  const tools=rows.filter(entry=>entry.role==='tool'&&ids.includes(entry.tool_call_id));
  if(ids.some(id=>tools.filter(entry=>entry.tool_call_id===id).length!==1))continue;
  paired.set(row,[row,...tools]);
 }
 const delivered=Array.from(paired.keys()).filter(row=>typeof row.deliveredAt==='number').sort((a,b)=>a.deliveredAt!-b.deliveredAt!);
 if(!delivered.length)return rows;
 // Build all protocol blocks, including unfinished carriers, before placing
 // completions. An anchor on an assistant means after its entire tool block.
 const tools=new Set(Array.from(paired.values()).flatMap(block=>block.slice(1)));
 const moving=new Set(delivered);
 const result:T[][]=rows.filter(row=>!tools.has(row)&&!moving.has(row)).map(row=>paired.get(row)??[row]);
 for(const row of delivered){
  let after=-1;
  for(let index=0;index<result.length;index++){
   const block=result[index],candidate=block[0];
   if(row.deliveredAfterIds ? block.some(entry=>!!entry.id&&row.deliveredAfterIds!.includes(entry.id)) : (candidate.deliveredAt??timeOf(candidate))<=row.deliveredAt!) after=index;
  }
  // Old rows can acquire a timestamp during recovery that is newer than the
  // answer's saved delivery time. Never move the answer ahead of its request.
  const sourceUser=rows.slice(0,rows.indexOf(row)).findLast(entry=>entry.role==='user');
  if(sourceUser)after=Math.max(after,result.findIndex(block=>block.includes(sourceUser)));
  result.splice(after+1,0,paired.get(row)!);
 }
 return result.flat();
}
export function deliverChatReply<T extends ChatDeliveryEntry>(rows:T[],id:string,patch:Partial<T>,now=Date.now()):T[]{
 return orderChatDeliveries(rows.map(row=>row.id===id?{...row,...patch,deliveredAt:row.deliveredAt??now,deliveredAfterIds:row.deliveredAfterIds??rows.filter(other=>other.id!==id && (other.role==='user'||typeof other.deliveredAt==='number')).flatMap(other=>other.id?[other.id]:[])}:row));
}
export class ActiveChatRequests<T>{
 private readonly requests=new Map<string|number,Set<T>>();
 add(project:string|number,request:T){const active=this.requests.get(project)??new Set<T>();active.add(request);this.requests.set(project,active);}
 delete(project:string|number,request:T){const active=this.requests.get(project);active?.delete(request);if(!active?.size)this.requests.delete(project);}
 has(project:string|number){return !!this.requests.get(project)?.size;}
 hasOther(project:string|number,request:T){return Array.from(this.requests.get(project)??[]).some(value=>value!==request);}
}
